import React, { useState } from 'react';

// Simple CSV parser for comma-separated values, handling quoted fields
function parseCSV(text) {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
  const latIdx = headers.findIndex(h => h === 'incident latitude');
  const longIdx = headers.findIndex(h => h === 'incident longitude');
  if (latIdx === -1 || longIdx === -1) return [];

  return lines.slice(1).map(line => {
    // Handle quoted fields with commas
    const cols = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        cols.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    cols.push(current);

    // Remove whitespace and quotes
    const clean = (val) => {
      let v = val.trim();
      if (v.startsWith('"') && v.endsWith('"')) {
        v = v.slice(1, -1).replace(/""/g, '"');
      }
      return v;
    };

    return {
      lat: clean(cols[latIdx] || ''),
      long: clean(cols[longIdx] || ''),
      row: cols.map(clean),
    };
  }).filter(row => row.lat && row.long);
}

function GetAddress() {
  const [apiKey, setApiKey] = useState('91ade01c27c0402d90f454d7361f84b0');
  const [csvRows, setCsvRows] = useState([]); // [{lat, long, row}]
  const [addresses, setAddresses] = useState([]); // [{lat, long, address, status}]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [csvFileName, setCsvFileName] = useState('');

  const getAddressFromLatLong = async (apiKey, lat, long) => {
    if (!apiKey) throw new Error('API key is required');
    if (lat === undefined || long === undefined || lat === '' || long === '') throw new Error('Latitude and longitude are required');
    const url = `https://api.opencagedata.com/geocode/v1/json?q=${encodeURIComponent(lat)},${encodeURIComponent(long)}&key=${encodeURIComponent(apiKey)}&no_annotations=1&limit=1`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Geocoding API error: ${response.status}`);
    }
    const data = await response.json();
    if (data.results && data.results.length > 0) {
      return data.results[0].formatted;
    } else {
      throw new Error('No address found for the given coordinates.');
    }
  };

  const handleFileUpload = (e) => {
    setError('');
    setCsvRows([]);
    setAddresses([]);
    const file = e.target.files[0];
    if (!file) return;
    setCsvFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const rows = parseCSV(event.target.result);
        if (!rows.length) {
          setError('No valid latitude/longitude found in CSV.');
        }
        setCsvRows(rows);
      } catch (err) {
        setError('Failed to parse CSV: ' + (err.message || err));
      }
    };
    reader.readAsText(file);
  };

  const handleGetAddresses = async (e) => {
    e.preventDefault();
    setAddresses([]);
    setError('');
    if (!apiKey) {
      setError('Please enter API key.');
      return;
    }
    if (!csvRows.length) {
      setError('Please upload a CSV file with incident latitude and longitude.');
      return;
    }
    setLoading(true);
    const results = [];
    for (let i = 0; i < csvRows.length; i++) {
      const { lat, long } = csvRows[i];
      try {
        // eslint-disable-next-line no-await-in-loop
        const address = await getAddressFromLatLong(apiKey, lat, long);
        results.push({ lat, long, address, status: 'OK' });
      } catch (err) {
        results.push({ lat, long, address: '', status: err.message || 'Failed' });
      }
      // Add a small delay to avoid rate limits
      // eslint-disable-next-line no-await-in-loop
      await new Promise(res => setTimeout(res, 1200));
      setAddresses([...results]);
    }
    setLoading(false);
  };

  return (
    <div style={{ maxWidth: 600, margin: '2rem auto', padding: 24, border: '1px solid #ccc', borderRadius: 8, fontFamily: 'Segoe UI, Arial, sans-serif' }}>
      <h2>Reverse Geocode (Lat/Long to Address from CSV)</h2>
      <form onSubmit={handleGetAddresses} style={{ display: 'grid', gap: 16 }}>
        <div>
          <label>API Key:</label>
          <input
            type="password"
            value={apiKey}
            onChange={e => setApiKey(e.target.value)}
            placeholder="Enter API key"
            style={{ width: '100%', padding: 8, marginTop: 4 }}
            required
          />
        </div>
        <div>
          <label>Upload CSV File:</label>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            style={{ width: '100%', padding: 8, marginTop: 4 }}
            required
          />
          <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>
            CSV must have headers: <code>incident latitude</code>, <code>incident longitude</code>
          </div>
          {csvFileName && (
            <div style={{ marginTop: 6, color: '#555', fontSize: 13 }}>
              Loaded file: <strong>{csvFileName}</strong> ({csvRows.length} rows)
            </div>
          )}
        </div>
        <button
          type="submit"
          disabled={loading || !csvRows.length}
          style={{
            padding: '10px 16px',
            background: '#6A1B9A',
            color: 'white',
            border: 'none',
            borderRadius: 6,
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Getting Addresses...' : 'Get Addresses'}
        </button>
      </form>
      {error && <div style={{ color: '#b00020', marginTop: 16 }}>{error}</div>}
      {addresses.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3>Results</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
            <thead>
              <tr style={{ background: '#ede7f6' }}>
                <th style={{ border: '1px solid #ccc', padding: 6 }}>Latitude</th>
                <th style={{ border: '1px solid #ccc', padding: 6 }}>Longitude</th>
                <th style={{ border: '1px solid #ccc', padding: 6 }}>Address</th>
              </tr>
            </thead>
            <tbody>
              {addresses.map((row, idx) => (
                <tr key={idx} style={{ background: idx % 2 ? '#f3e5f5' : 'white' }}>
                  <td style={{ border: '1px solid #ccc', padding: 6 }}>{row.lat}</td>
                  <td style={{ border: '1px solid #ccc', padding: 6 }}>{row.long}</td>
                  <td style={{ border: '1px solid #ccc', padding: 6 }}>{row.address}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default GetAddress;
